import type {
  ConversationEntry,
  DialogState,
  PatientInterviewState,
} from '../types';

export interface VideoStartPayload {
  contentType?: unknown;
  content_type?: unknown;
  [key: string]: unknown;
}

export interface SegmentTextPayload {
  text?: unknown;
  fsmState?: unknown;
  patientState?: unknown;
  timing?: unknown;
  [key: string]: unknown;
}

export interface InterviewResponseMessageDependencies {
  nowMs: () => number;
  nowDate: () => Date;
  enqueueResponseClip: (data: ArrayBuffer, mime: string) => void;
  appendConversation: (entry: ConversationEntry) => void;
  setFsmState: (state: DialogState) => void;
  setPatientState: (state: PatientInterviewState) => void;
  setLastTiming: (timing: Record<string, number>) => void;
  setCurrentInterimText: (text: string) => void;
  setIsAwaitingResponse: (value: boolean) => void;
  setIsPlayingResponse: (value: boolean) => void;
  setIsRecording: (value: boolean) => void;
  setRecordingTime: (value: number) => void;
  debugLog: (...args: unknown[]) => void;
}

const DEFAULT_VIDEO_MIME = 'video/mp4';

/**
 * 患者応答テキストと応答動画バイナリの受信順序を管理する。
 * React stateと再生キューへの反映はdependenciesへ委譲する。
 */
export class InterviewResponseMessageController {
  private expectingVideoBinary = false;
  private currentVideoMime = DEFAULT_VIDEO_MIME;

  constructor(private readonly dependencies: InterviewResponseMessageDependencies) {}

  handleVideoStart(payload: VideoStartPayload): void {
    this.expectingVideoBinary = true;
    this.currentVideoMime = (
      payload.contentType
      || payload.content_type
      || DEFAULT_VIDEO_MIME
    ) as string;
    this.dependencies.debugLog('[Client] video_start received:', payload);
    this.dependencies.setIsAwaitingResponse(false);
    this.dependencies.setIsPlayingResponse(true);
  }

  handleBinary(data: ArrayBuffer): void {
    if (!this.expectingVideoBinary) {
      this.dependencies.debugLog(
        '[Client] binary received but not expecting video, ignoring',
        data.byteLength,
      );
      return;
    }

    this.expectingVideoBinary = false;
    this.dependencies.debugLog(
      `[Client] video binary received: bytes=${data.byteLength}, mime=${this.currentVideoMime}`,
    );
    this.dependencies.enqueueResponseClip(data, this.currentVideoMime);
  }

  handleVideoEnd(payload: Record<string, unknown>): void {
    this.expectingVideoBinary = false;
    this.dependencies.debugLog('[Client] video_end received:', payload);
  }

  handleSegmentText(payload: SegmentTextPayload): void {
    const {
      text,
      fsmState,
      patientState,
      timing,
    } = payload;
    this.dependencies.debugLog('[Client] segment_text received:', { text });

    if (fsmState) {
      this.dependencies.setFsmState(fsmState as DialogState);
    }
    if (patientState) {
      this.dependencies.setPatientState(patientState as PatientInterviewState);
    }
    if (timing) {
      this.dependencies.setLastTiming(timing as Record<string, number>);
    }
    this.dependencies.setCurrentInterimText('');

    if (text) {
      this.dependencies.appendConversation({
        id: `msg-response-${this.dependencies.nowMs()}`,
        type: 'response',
        text: text as string,
        timestamp: this.dependencies.nowDate(),
      });
    }

    this.dependencies.setIsAwaitingResponse(false);
    this.dependencies.setIsRecording(false);
    this.dependencies.setRecordingTime(0);
  }
}
